import type { ReactNode } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { api, useApi } from '../api/queries'
import type { ApiError } from '../api/problem'
import type { components } from '../api/schema'
import type { BanReplay, RaceSummary } from '../api/types'
import type { Removals } from '../chart/SeasonProgression'
import { ConstructorStatsTable, DriverStatsTable, NameFilter } from '../components/CareerTables'
import { DataState } from '../components/DataState'
import { useUniversePath } from '../lib/universe'
import type { SeasonsRequest, Universe } from './PeoplePages'
import { Drivers, Standings } from './SeasonPage'

type RacePage = components['schemas']['Page_RaceSummary_']

/**
 * A history replayed on the visitor's own terms: the bans of "With Ban", the
 * points table of "Custom Points". Each category says how through one hook,
 * and the four pages below draw whatever it returns.
 */
export type Source = {
  /** The category's first page, where its choice is made: `/ban`, `/points`. */
  path: string
  /** Ends every title: "Champions with the bans". */
  suffix: string
  use: () => {
    /** Whether anything has been chosen yet; until then no page asks the server. */
    chosen: boolean
    /** The choice as a `?…` string, carried from page to page. */
    search: string
    replay: UseQueryResult<BanReplay, ApiError>
    /** What was chosen, with the way back to change it -- or the way there. */
    summary: ReactNode
    /** How one season's standings routes are asked for the same replay. */
    seasonQuery: (races: RaceSummary[]) => Removals
    /** How one driver's seasons are asked for in the same replay. */
    driverSeasons: (slug: string) => SeasonsRequest
  }
}

export function ReplayChampionshipPage({ source }: { source: Source }) {
  const { chosen, search, replay, summary } = source.use()
  const join = search ? `&${search.slice(1)}` : ''

  return (
    <section>
      <h1>Champions {source.suffix}</h1>
      {summary}
      {chosen ? (
        <DataState query={replay} empty="No seasons.">
          {(data) => (
            <div className="table-scroll">
              <table className="grid">
                <thead>
                  <tr>
                    <th scope="col">Year</th>
                    <th scope="col">Drivers’ champion</th>
                    <th scope="col">Constructors’ champion</th>
                  </tr>
                </thead>
                <tbody>
                  {data.champions.map((row) => (
                    <tr key={row.year}>
                      <td>
                        <Link to={`${source.path}/season?y=${row.year}${join}`}>
                          {row.year}
                        </Link>
                      </td>
                      {row.complete ? (
                        <>
                          <td>
                            <Named entity={row.driver} path="drivers" />
                          </td>
                          <td>
                            <Named entity={row.constructor} path="constructors" />
                          </td>
                        </>
                      ) : (
                        <td colSpan={2} className="muted">
                          In progress
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DataState>
      ) : null}
    </section>
  )
}

function Named({
  entity,
  path,
}: {
  entity: { slug: string; name: string } | null | undefined
  path: 'drivers' | 'constructors'
}) {
  const to = useUniversePath()
  if (!entity) return <span className="muted">—</span>
  return <Link to={to(`/${path}/${entity.slug}`)}>{entity.name}</Link>
}

export function ReplayDriversPage({ source }: { source: Source }) {
  const { chosen, replay, summary } = source.use()
  return (
    <section>
      <h1>Drivers {source.suffix}</h1>
      {summary}
      {chosen ? (
        <>
          <NameFilter label="Search drivers" />
          <DataState query={replay} empty="No drivers.">
            {(data) => <DriverStatsTable rows={data.drivers} />}
          </DataState>
        </>
      ) : null}
    </section>
  )
}

export function ReplayConstructorsPage({ source }: { source: Source }) {
  const { chosen, replay, summary } = source.use()
  return (
    <section>
      <h1>Constructors {source.suffix}</h1>
      {summary}
      {chosen ? (
        <>
          <NameFilter label="Search constructors" />
          <DataState query={replay} empty="No constructors.">
            {(data) => <ConstructorStatsTable rows={data.constructors} />}
          </DataState>
        </>
      ) : null}
    </section>
  )
}

/** The same replay, for a driver's page inside it. */
export function universeOf(source: Source): Universe {
  return {
    path: source.path,
    suffix: source.suffix,
    use: function useReplayUniverse() {
      const { chosen, search, summary, replay, driverSeasons } = source.use()
      return { chosen, search, summary, careers: replay, driverSeasons }
    },
    // The races say which rounds a banned Grand Prix was.
    useRemovals: function useReplayRemovals(year) {
      const { seasonQuery } = source.use()
      const races = useApi<RacePage>(['races', year], () =>
        api.GET('/api/v1/seasons/{year}/races', {
          params: { path: { year }, query: { limit: 30 } },
        }),
      )
      return races.data ? seasonQuery(races.data.items) : undefined
    },
  }
}

const TABS = [
  { key: 'drivers', label: 'Drivers' },
  { key: 'constructors', label: 'Constructors' },
] as const

/** One season of the replay, drawn as `/seasons/:year` draws it. */
export function ReplaySeasonPage({ source }: { source: Source }) {
  const [params, setParams] = useSearchParams()
  const { chosen, replay, summary, seasonQuery } = source.use()
  const years = (replay.data?.champions ?? []).map((row) => row.year)
  const asked = Number(params.get('y'))
  const year = years.includes(asked) ? asked : years[0]
  const tab = params.get('tab') === 'constructors' ? 'constructors' : 'drivers'
  const races = useApi<RacePage>(
    ['races', year],
    () =>
      api.GET('/api/v1/seasons/{year}/races', {
        params: { path: { year: year ?? 0 }, query: { limit: 30 } },
      }),
    { enabled: year !== undefined },
  )
  // Pins name one championship's competitors, so they do not survive a switch.
  const choose = (next: { y?: string; tab?: string }) => {
    const updated = new URLSearchParams(params)
    updated.delete('pin')
    for (const [key, value] of Object.entries(next)) {
      if (value) updated.set(key, value)
      else updated.delete(key)
    }
    setParams(updated, { replace: true })
  }

  return (
    <section>
      <h1>A season {source.suffix}</h1>
      {summary}
      {chosen ? (
        <DataState query={replay}>
          {() => (
            <>
              <label className="search">
                <span className="muted">Season</span>
                <select
                  value={year ?? ''}
                  onChange={(event) => choose({ y: event.target.value })}
                >
                  {years.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>{' '}
              <Link to={`/seasons/${year}`}>The season as it happened →</Link>
              <nav className="tabs">
                {TABS.map((entry) => (
                  <button
                    key={entry.key}
                    type="button"
                    className={entry.key === tab ? 'tabs__tab tabs__tab--on' : 'tabs__tab'}
                    aria-current={entry.key === tab}
                    onClick={() => choose({ tab: entry.key === 'drivers' ? '' : entry.key })}
                  >
                    {entry.label}
                  </button>
                ))}
              </nav>
              {/* The races say which rounds a banned Grand Prix was; until they
                  arrive the season would be drawn with them still in. */}
              {year !== undefined && races.data ? (
                tab === 'drivers' ? (
                  <Drivers year={year} removals={seasonQuery(races.data.items)} />
                ) : (
                  <Standings
                    year={year}
                    championship="constructors"
                    removals={seasonQuery(races.data.items)}
                  />
                )
              ) : null}
            </>
          )}
        </DataState>
      ) : null}
    </section>
  )
}
